import React from 'react';
import { ConfigSelectorOption } from './entities';
import styled, { css } from '@benzinga/themetron';
import { MdSave } from 'react-icons/md';
import { FaRegStar, FaStar } from 'react-icons/fa';

export interface ConfigSelectorBoxProps {
  resourceName: string;
  selectedConfig?: ConfigSelectorOption;
  onBoxClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onFavoriteClick?: (id: string, favorite: boolean) => void;
  onSave: (config: ConfigSelectorOption) => void;
  saveTooltip?: string;
  saveEnabled?: boolean;
}

const OptionBox: React.FC<{
  selectedConfig?: ConfigSelectorOption;
  saveEnabled: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  resourceName: string;
}> = props => {
  const selectedConfig = props.selectedConfig;
  const displayText = selectedConfig?.displayText ?? `Select a Config`;
  const tooltipText = selectedConfig?.tooltipText ?? displayText;
  return (
    <NameBox $emphasize={props.saveEnabled} onClick={props.onClick} title={tooltipText}>
      {displayText}
    </NameBox>
  );
};

export const ConfigSelectorBox: React.FC<ConfigSelectorBoxProps> = props => {
  const favButton = React.useMemo(() => {
    const onFavorite = props.onFavoriteClick;
    const selectedConfig = props.selectedConfig;
    if (!onFavorite) {
      return null;
    }

    return (
      <FavIconWrapper>
        {selectedConfig?.favorite ? (
          <StyledFavIconSolid
            onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
              e.stopPropagation();
              if (!selectedConfig) {
                throw new Error('Should not be able to click favorite without a selected config');
              }
              onFavorite(selectedConfig.id, false);
            }}
          >
            <FaStar />
          </StyledFavIconSolid>
        ) : (
          <StyledFavIconReg
            $active={selectedConfig != null}
            onClick={() => {
              if (!selectedConfig) {
                throw new Error('Should not be able to click favorite without a selected config');
              }
              onFavorite(selectedConfig.id, true);
            }}
          >
            <FaRegStar />
          </StyledFavIconReg>
        )}
      </FavIconWrapper>
    );
  }, [props.onFavoriteClick, props.selectedConfig]);

  const saveButton = React.useMemo(() => {
    const onSave: React.MouseEventHandler = e => {
      e.preventDefault();
      e.stopPropagation();
      const onSave = props.onSave;
      const selectedItem = props.selectedConfig;
      if (selectedItem == null) {
        throw new Error('Should not be able to click save without a selected config');
      }
      onSave(selectedItem);
    };

    // if we haven't typed anything and also don't have a selected config, then we should disable the save button
    const saveActive = (props.saveEnabled ?? false) && props.selectedConfig != null;

    const tooltip = (() => {
      if (props.saveTooltip) {
        return props.saveTooltip;
      }
      if (saveActive) {
        return `Save ${props.resourceName}`;
      } else {
        return `Saving is disabled`;
      }
    })();

    return (
      <SaveIconWrapper>
        <StyledSaveIcon $active={saveActive} onClick={onSave} title={tooltip} />
      </SaveIconWrapper>
    );
  }, [props.onSave, props.resourceName, props.saveEnabled, props.saveTooltip, props.selectedConfig]);

  return (
    <BoxWrapper>
      {favButton}
      <InnerBoxWrapper>
        <OptionBox
          onClick={props.onBoxClick}
          resourceName={props.resourceName}
          saveEnabled={props.saveEnabled ?? false}
          selectedConfig={props.selectedConfig}
        />
      </InnerBoxWrapper>
      {saveButton}
    </BoxWrapper>
  );
};

const BoxWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-grow: 1;
  container-type: size;
  /* border-left: 1px solid ${({ theme }) => theme.colors.border}; */
  /* border-left: unset; */
`;

const InnerBoxWrapper = styled.div`
  min-width: 40px;
  padding-left: 17cqh;
  padding-right: 32cqh;
  flex-grow: 1;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
`;

const NameBox = styled.div<{ $emphasize: boolean }>`
  font-style: ${({ $emphasize }) => ($emphasize ? 'italic' : 'normal')};
  font-weight: ${({ $emphasize }) => ($emphasize ? 'bold' : 'normal')};
  &:hover {
    color: ${({ theme }) => theme.colors.foregroundActive};
  }
  width: 100%;
  height: 100%;
  cursor: pointer;
  align-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SaveIconWrapper = styled.div`
  height: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  box-sizing: content-box;
  /* border-right: 1px solid ${({ theme }) => theme.colors.border}; */
`;

const StyledSaveIcon = styled(MdSave)<{ $active: boolean }>`
  fill: ${({ $active, theme }) => ($active ? theme.colors.foregroundInactive : theme.colors.border)};
  width: 100%;
  height: 100%;
  padding: 25cqh;
  ${({ $active }) =>
    $active
      ? css`
          cursor: pointer;
        `
      : ''};
`;

const FavIconWrapper = styled.div`
  height: 100%;
  aspect-ratio: 0.8;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
`;

const StyledFavIconReg = styled.span<{ $active: boolean }>`
  svg {
    width: 100%;
    height: 100%;
    padding: 32cqh;
    padding-right: 0px;
    ${({ $active }) =>
      $active
        ? css`
            fill: theme.colors.foreground;
          `
        : css`
            fill: ${props => props.theme.colors.border};
          `}
  }

  ${({ $active }) =>
    $active &&
    css`
      cursor: pointer;
      &:hover {
        svg {
          fill: ${props => props.theme.colorPalette.yellow400};
        }
      }
    `}
`;

const StyledFavIconSolid = styled.span`
  svg {
    fill: ${({ theme }) => theme.colorPalette.yellow400};
    width: 100%;
    height: 100%;
    padding: 32cqh;
    padding-right: 0px;
    cursor: pointer;
  }
`;
